/**
 * iQulture score DISPLAY formatting — the single rendering of a CheckUp score.
 *
 * Pure, platform-agnostic — no React, no DOM, no RN — so journey-web,
 * citizen-mobile and business-web all render one score one way. It sits beside
 * `score-scale.ts` for the same reason that file exists: three inline copies
 * drift, and a shared module cannot.
 *
 * WHY THIS EXISTS AT ALL
 * The same `iqultureScore` rendered TWO ways above the fold on mobile Home —
 * `73.46` in the CheckUp banner (`Math.round(n * 100) / 100`) and `73.5` in the
 * hero-insights card (`Math.round(n * 10) / 10`) — one scroll position apart.
 * Platform-wide there were eleven divergent sites: 2dp, 1dp, 0dp, raw float,
 * `%`-suffixed, and one hand-split integer/decimal pair that was outright
 * WRONG: `Math.floor(73.96)` with `Math.round((73.96 - 73) * 10)` renders
 * `73` and `10` — "73.10" — because the tenths carry into the units and the
 * split never propagates it.
 *
 * THE CANONICAL RENDERING is one decimal place with a trailing `.0` trimmed,
 * and an em dash when there is no score. It is the behaviour journey-web's
 * CheckUp results view already shipped (the former feature-local `fmt1`), which
 * makes it the one choice that changes nothing a citizen has already seen on
 * the surface where the score is the headline.
 *
 * `formatIQultureScoreParts` exists for the one legitimate case where the two
 * halves are styled differently (a large integer with small-print tenths). It
 * is derived FROM the same string, so the pair can never disagree with the
 * whole.
 */

/** Rendered in place of a score that does not exist yet. */
const NO_SCORE = '—';

/**
 * Format an iQulture score (0–100) for display, in the reader's locale.
 *
 * One decimal place, trailing `.0` trimmed, em dash when absent — and the
 * locale's decimal separator (`73,5` in Portuguese), never a hard-coded `.`.
 * No unit: the score is a 0–100 scale, not a percentage.
 *
 * @example
 * formatIQultureScore(73.456)          // "73.5"
 * formatIQultureScore(73.456, 'pt-PT') // "73,5"
 * formatIQultureScore(73.96)           // "74"   (rounds up through the units)
 * formatIQultureScore(46)              // "46"   (not "46.0")
 * formatIQultureScore(null)            // "—"
 */
export function formatIQultureScore(score: number | null | undefined, locale = 'en'): string {
  if (score == null || !Number.isFinite(score)) return NO_SCORE;
  return scoreFormat(locale).format(roundIQultureScore(score));
}

/**
 * The score at display precision (one decimal) as a NUMBER — for a value
 * formatted later in the reader's locale (a notification rendered in the
 * recipient's language formats its numbers itself).
 */
export function roundIQultureScore(score: number): number {
  return Math.round(score * 10) / 10;
}

/**
 * The same rendering, split into the parts a two-weight layout needs.
 *
 * `integer` is the headline digits and `fraction` is the `,5` / `.5` small
 * print (an EMPTY string when the rounded score is whole, so the caller renders
 * nothing rather than a bare separator). Both come from the same formatted
 * string, so `73.96` yields `{ integer: '74', fraction: '' }` — never the `74` +
 * `.10` that a hand-rolled floor/round pair produces.
 *
 * When there is no score, `integer` carries the em dash and `fraction` is empty.
 *
 * Split at the locale's own decimal separator, read from the same format —
 * not with `formatToParts`, which Hermes (the phone's engine) does not have on
 * `Intl.NumberFormat`: the CheckUp result screen threw "undefined is not a
 * function" on every phone (1 Oct 2026).
 */
export function formatIQultureScoreParts(
  score: number | null | undefined,
  locale = 'en'
): {
  integer: string;
  fraction: string;
} {
  if (score == null || !Number.isFinite(score)) return { integer: NO_SCORE, fraction: '' };
  const rounded = roundIQultureScore(score);
  const format = scoreFormat(locale);
  const text = format.format(rounded);
  // A whole score has no fraction to split off.
  if (Number.isInteger(rounded)) return { integer: text, fraction: '' };
  const at = text.lastIndexOf(decimalSeparator(locale, format));
  if (at < 0) return { integer: text, fraction: '' };
  return { integer: text.slice(0, at), fraction: text.slice(at) };
}

/** The locale's decimal separator, as this format writes it (`.` / `,` / `٫`). */
const separators = new Map<string, string>();
function decimalSeparator(locale: string, format: Intl.NumberFormat): string {
  let separator = separators.get(locale);
  if (separator === undefined) {
    // 1.5 has no grouping: what sits between its digits is the separator.
    separator = format.format(1.5).replace(/[\d\p{Nd}]/gu, '');
    separators.set(locale, separator);
  }
  return separator;
}

const formats = new Map<string, Intl.NumberFormat>();
function scoreFormat(locale: string): Intl.NumberFormat {
  let format = formats.get(locale);
  if (!format) {
    format = new Intl.NumberFormat(locale, { minimumFractionDigits: 0, maximumFractionDigits: 1 });
    formats.set(locale, format);
  }
  return format;
}
